import { useState, useEffect } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Plus, Search, Loader2, AlertCircle, RefreshCw, Utensils, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useMenuTypes } from '../../hooks/useMenuTypes';
import { useOrders } from '../../hooks/useOrders';
import { usePageTitle } from '../../hooks/usePageTitle';
import { CreateMenuTypeDto } from '../../services/menuTypes.service';
import { toast } from 'sonner';

interface MenuTypesPageProps {
  onNavigate?: (page: string) => void;
  onMenuTypeClick?: (menuTypeId: string) => void;
}

export function MenuTypesPage({ onNavigate, onMenuTypeClick }: MenuTypesPageProps) {
  usePageTitle('Menu Types');

  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { user: authUser } = useAuth();
  const { orderByTable } = useRestaurant();
  const { menuTypes, loading, submitting, error, createMenuType, refresh } = useMenuTypes(authUser?.tenantId || authUser?.restaurantId);

  const { orders: ordersData, stats } = useOrders({ page: 1, limit: 50 });

  // Debug logging
  useEffect(() => {
    console.log('MenuTypesPage - Component mounted/updated');
    console.log('MenuTypesPage - orderByTable from context:', orderByTable);
  }, [orderByTable]);

  const filteredMenuTypes = menuTypes.filter((menuType) =>
    menuType.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleMenuTypeClick = (menuTypeId: string) => {
    // Store the selected menu type ID in localStorage for later use
    localStorage.setItem('selectedMenuTypeId', menuTypeId);
    
    // console.log('MenuTypesPage - Menu type clicked:', menuTypeId);
    // console.log('MenuTypesPage - orderByTable:', orderByTable);
    
    if (orderByTable) {
      // If table-based ordering is enabled, navigate to tables page first
      // console.log('MenuTypesPage - Redirecting to tables page');
      onNavigate?.('tables');
    } else if (onMenuTypeClick) {
      // console.log('MenuTypesPage - Calling onMenuTypeClick');
      onMenuTypeClick(menuTypeId);
    } else {
      // Navigate directly to categories page for this menu type
      // console.log('MenuTypesPage - Navigating to menus');
      onNavigate?.('menus');
    }
  };

  const handleCreateMenuType = async (data: CreateMenuTypeDto) => {
    try {
      await createMenuType(data);
      setShowCreateModal(false);
      toast.success('Menu type created successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create menu type');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="menuTypes"
        onNavigate={onNavigate}
        ordersCount={stats?.total || ordersData.length}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Menu Types</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">
                  Organize your menu into different types (Restaurant, Drinks, etc.)
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={refresh}
                  disabled={loading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>

                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Add Menu Type</span>
                </button>
              </div>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="flex items-center gap-3 p-4 mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search menu types..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Menu Types Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading menu types…</p>
                </div>
              </div>
            ) : filteredMenuTypes.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Utensils className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {searchQuery ? 'No menu types found' : 'No menu types yet'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {searchQuery ? 'Try adjusting your search query' : 'Create your first menu type to get started'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Menu Type
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredMenuTypes.map((menuType) => (
                  <button
                    key={menuType.id}
                    onClick={() => handleMenuTypeClick(menuType.id)}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-lg hover:scale-105 transition-all duration-200 text-left group relative"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">
                          {menuType.icon || '🍽️'}
                        </div>
                        <div className="flex gap-1">
                          <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            menuType.isActive
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}>
                            {menuType.isActive ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {menuType.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
                        {menuType.description || 'No description'}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                              {menuType._count?.categories || 0}
                            </span>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {menuType._count?.categories === 1 ? 'category' : 'categories'}
                          </span>
                        </div>
                        <div className="text-indigo-600 dark:text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                  </button>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateMenuTypeModal
          tenantId={authUser?.tenantId || authUser?.restaurantId || ''}
          submitting={submitting}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateMenuType}
        />
      )}
    </div>
  );
}

// ── CreateMenuTypeModal ──────────────────────────────────────────────────────

interface CreateMenuTypeModalProps {
  tenantId: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (data: CreateMenuTypeDto) => Promise<void>;
}

const MENU_TYPE_ICONS = ['🍽️', '🍕', '🍔', '🍜', '🥗', '🍛', '☕', '🍹', '🍺', '🍷', '🧋', '🥤'];

function CreateMenuTypeModal({ tenantId, submitting, onClose, onSubmit }: CreateMenuTypeModalProps) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    icon: '🍽️',
    sortOrder: 0,
    isActive: true,
  });
  const [formError, setFormError] = useState<string | null>(null);

  const set = (k: keyof typeof form, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!form.name.trim()) return setFormError('Name is required');

    try {
      await onSubmit({
        name: form.name,
        description: form.description || undefined,
        icon: form.icon,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
        tenantId,
      });
    } catch (err: any) {
      setFormError(err.message || 'Something went wrong');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create Menu Type</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Add a new menu type to organize your menu</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {formError}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g., Restaurant, Drinks, Desserts"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Brief description of this menu type"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Icon Selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Icon</label>
            <div className="grid grid-cols-6 gap-2">
              {MENU_TYPE_ICONS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => set('icon', icon)}
                  className={`p-3 text-2xl rounded-lg border-2 transition-all ${
                    form.icon === icon
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Sort Order</label>
            <input
              type="number"
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Active Status</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Menu type is visible when active</p>
            </div>
            <button
              type="button"
              onClick={() => set('isActive', !form.isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.isActive ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                form.isActive ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Menu Type
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}